import { Button, Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import React, { memo, useEffect, useRef, useState } from 'react';
import { FormGridEditable } from '../../../components/hook-form/form-grid-editable.component';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { Service } from '../../../models/masters/service';
import { servicesApiKeyStore } from '../../../api/masters/services-api-key-store';
import { GridColumnConfig } from '../../../models/gridCoumnConfig';
import { FieldErrors, useFormContext } from 'react-hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDeleteData } from '../../../hooks/useDeleteData';
import { workOrdersApiKeyStore } from '../../../api/workorders-api-key-store';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { workorderEmployeeColumn } from './work-order-employee_column';
import { useAuthorize } from '../../../hooks/use-authorize';
import { UserPermissionName } from '../../../models/user-permission-name';

interface IWorkOrderEmployeeGrid {
  data: any[];
  refetch: () => void;
  name: string;
}

interface CustomGridCellProps {
  field: string;
}
interface CellProps {
  timeAndMeterial: any;
}

interface MergedItem {
  [key: string]: any;
}

const mergeDataItems = (array1: any[], array2: any[], key: string): MergedItem[] => {
  const mappedArray = new Map();
  Array.isArray(array1) &&
    array1?.forEach((item) => {
      mappedArray.set(item[key], item);
    });

  Array.isArray(array2) &&
    array2?.forEach((item) => {
      mappedArray.set(item[key], item);
    });

  return Array.from(mappedArray.values());
};

const WorkOrderEmployeeGrid = ({ data, refetch, name }: IWorkOrderEmployeeGrid) => {
  const {
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();
  // Step 1: Manage state for data
  const [dataItems, setDataItems] = useState(data || []);
  const [currentItem, setCurrentIteam] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const {
    isLoading: isLoadingDelete,
    isError,
    error,
    mutateAsync: deleteTimeAndMeterial,
  } = useDeleteData(workOrdersApiKeyStore.deleteTimeandMaterial(currentItem));

  useEffect(() => {
    if (data) {
      setDataItems(data);
    }
  }, [data]);

  // Step 2: Handle "Add" button click
  const handleAddRow = () => {
    const newRow = {
      workOrderTimeAndMaterialDetailsID: 0,
      workOrderID: 0,
      workedHours: null,
      workedOn: '',
      employeeName: '',
      invoiceNumber: '',
      invoiceAmount: '',
      invoiceDate: '',
      invoiceNo: '',
      invoiceDueDate: '',
      temPId: `T-${dataItems?.length + 1}`,
    } as any;
    setDataItems([newRow, ...getValues(name)]); // Add new row at the top
  };

  const deleteRowLocally = (temPId: string) => {
    const updatedData = getValues(name)?.filter((item: any) => item.temPId !== temPId);
    setDataItems(updatedData);
  };

  const handleDeleteClick = (temPId: string, temandMeterialId: number) => {
    if (temPId.startsWith('o')) {
      setCurrentIteam(temandMeterialId);
      setIsOpen(true);
    } else if (temPId.startsWith('T')) {
      // If the first letter is 'T', delete the item locally
      deleteRowLocally(temPId);
    }
  };

  const OptionCell = ({ timeAndMeterial }: CellProps) => {
    return (
      <td
        style={{ cursor: 'pointer' }}
      //   onClick={handleClickWorkorderNumber}
      >
        <>
          {/* <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_SERVICE_BUNDLE]}> */}
          {!timeAndMeterial?.invoiceNo && (
            <IconButton
              style={{ padding: 5 }}
              aria-label="delete"
              onClick={() =>
                handleDeleteClick(timeAndMeterial?.temPId, timeAndMeterial?.workOrderTimeAndMaterialDetailsID)
              }
            >
              <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                <DeleteIcon className="btnDelete" />
              </Tooltip>
            </IconButton>
          )}
          {/* </GuardedComponent> */}
          {!!timeAndMeterial?.invoiceNo && (
            <IconButton style={{ padding: 5 }} aria-label="delete">
              <Tooltip title={<Typography variant="body1">Can not delete invoiced work details</Typography>}>
                <DeleteIcon style={{ color: 'gray' }} className="btnDelete" />
              </Tooltip>
            </IconButton>
          )}
        </>
      </td>
    );
  };

  const limitDecimalPlaces = (value: any) => {
    if (typeof value !== 'string') {
      throw new Error('Input must be a string');
    }

    // Find the decimal point
    const decimalIndex = value?.indexOf('.');

    if (decimalIndex === -1) {
      // If there's no decimal point, return the original value
      return value;
    }

    // Get the part after the decimal point
    const decimalPart = value.slice(decimalIndex + 1);

    if (decimalPart.length > 2) {
      // If more than two digits after the decimal point, truncate it
      const truncatedDecimalPart = decimalPart.slice(0, 2);
      return value.slice(0, decimalIndex + 1) + truncatedDecimalPart;
    }

    return value; // If there are 2 or fewer digits after the decimal, return the original value
  };

  const limitToHourMinuteFormat = (value: string): string => {
    let [integerPart, decimalPart] = value.split('.');

    // Ensure integerPart is a valid number
    integerPart = integerPart ? Math.max(parseInt(integerPart, 10), 0).toString() : '0';

    // Normalize decimalPart
    if (decimalPart) {
      decimalPart = decimalPart.slice(0, 2); // Limit to two digits

      if (decimalPart?.length === 1) {
        // If only one digit, pad with '0'

        decimalPart = decimalPart.padEnd(2, '0');
      }

      if (parseInt(decimalPart, 10) >= 60) {
        // If decimal part >= 60, increment integer and reset decimal to 00
        integerPart = (parseInt(integerPart, 10) + 1).toString();
        decimalPart = '00';
      } else {
        // If only one digit, normalize to two digits
        decimalPart = decimalPart.padEnd(2, '0');
      }
    } else {
      // If no decimal part, set it to '00'
      decimalPart = '00';
    }
    const finalValue = integerPart === '0' && decimalPart === '00' ? '' : `${integerPart}.${decimalPart}`;

    return finalValue;
  };

  const handleEditComplete = (value: any) => {
    const updatedData = mergeDataItems(dataItems, [value], 'temPId');
    setValue(name, updatedData);
    setDataItems(updatedData);
  };

  const NumericCellEditorHours = memo(({ timeAndMeterial }: CellProps) => {
    const [editableData, setEditableData] = useState(timeAndMeterial?.workedHours);

    const handleOnBlur = () => {
      const normalizedValue = limitToHourMinuteFormat(editableData || '');

      if (normalizedValue !== timeAndMeterial?.workedHours) {
        // setValue('hh', { ...dataItems, ['workedHours']: normalizedValue });
        handleEditComplete?.({ ...timeAndMeterial, ['workedHours']: normalizedValue });
      }

      setEditableData(normalizedValue);
    };

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let sanitizedValue = limitDecimalPlaces(e.target.value);
      sanitizedValue = sanitizedValue?.replace(/(\..*?)\..*/g, '$1'); // Allow only one dot
      setEditableData(sanitizedValue);
    };

    return (
      <td>
        {editableData !== undefined ? (
          !timeAndMeterial?.invoiceNo ? (
            <TextField
              value={editableData}
              onBlur={handleOnBlur}
              onChange={handleOnChange}
              variant="outlined"
              size="small"
              type="number"
            />
          ) : (
            <Tooltip title={<Typography variant="body1">Can not edit invoiced work details</Typography>}>
              <TextField
                value={editableData}
                onBlur={handleOnBlur}
                onChange={handleOnChange}
                variant="outlined"
                disabled={!!timeAndMeterial?.invoiceNo}
                size="small"
                type="number"
              />
            </Tooltip>
          )
        ) : (
          timeAndMeterial?.workedHours ?? ''
        )}
      </td>
    );
  });

  const DateCellEditor: React.FC<CellProps> = ({ timeAndMeterial }) => {
    const [editableData, setEditableData] = useState<dayjs.Dayjs | null>(
      timeAndMeterial?.workedOn ? dayjs(timeAndMeterial?.workedOn) : null
    );

    const handleOnChange = (newValue: dayjs.Dayjs | null) => {
      if (newValue && dayjs(newValue).isValid()) {
        const year = newValue.year();
        if (year >= 1500 && year <= 4000) {
          setEditableData(newValue);

          if (newValue.toISOString() !== timeAndMeterial?.workedOn) {
            handleEditComplete?.({ ...timeAndMeterial, workedOn: newValue.toISOString() });
          }
        }
      }
    };

    return (
      <td>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={editableData}
            onChange={handleOnChange}
            disabled={!!timeAndMeterial?.invoiceNo}
          // format="YYYY-MM-DD" // Adjust format as needed
          />
        </LocalizationProvider>
      </td>
    );
  };

  const NormalTextEditor: React.FC<CellProps> = ({ timeAndMeterial }) => {
    const [editableData, setEditableData] = useState(timeAndMeterial?.employeeName);

    const handleOnBlur = () => {
      if (editableData !== timeAndMeterial?.employeeName) {
        handleEditComplete?.({
          ...timeAndMeterial,
          employeeName: editableData,
        });
      }
    };

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditableData(e.target.value);
    };

    const handleEditClick = () => {
      setEditableData(timeAndMeterial?.employeeName ?? '');
    };

    return (
      <td onClick={handleEditClick}>
        {!timeAndMeterial?.invoiceNo ? (
          <TextField
            value={editableData}
            onBlur={handleOnBlur}
            onChange={handleOnChange}
            variant="outlined"
            size="small"
          //   disabled={!!timeAndMeterial?.invoiceNo}
          />
        ) : (
          <Tooltip title={<Typography variant="body1">Can not edit invoiced work details</Typography>}>
            <TextField
              value={editableData}
              onBlur={handleOnBlur}
              onChange={handleOnChange}
              variant="outlined"
              size="small"
              disabled={!!timeAndMeterial?.invoiceNo}
            />
          </Tooltip>
        )}
      </td>
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
    if (field === 'option') {
      return (dataItem: any) => {
        return <OptionCell timeAndMeterial={dataItem?.dataItem} />;
      };
    }
    if (field === 'workedHours') {
      return (dataItem: any) => {
        return <NumericCellEditorHours timeAndMeterial={dataItem?.dataItem} />;
      };
    }
    if (field === 'workedOn') {
      return (dataItem: any) => {
        return <DateCellEditor timeAndMeterial={dataItem?.dataItem} />;
      };
    }
    if (field === 'employeeName') {
      return (dataItem: any) => {
        return <NormalTextEditor timeAndMeterial={dataItem?.dataItem} />;
      };
    }
    // Return undefined if the field is not 'bluePrint'
    return undefined;
  };

  const closeDialog = () => {
    setIsOpen(false);
  };

  const submitHanlder = async () => {
    await deleteTimeAndMeterial();
    refetch();
    closeDialog();
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <div>
      <Grid container justifyContent="flex-end">
        <Grid item>
          <Button style={{ marginBottom: 12 }} onClick={handleAddRow} variant="contained" color="primary">
            Add work details
          </Button>
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <FormGridEditable
            fileTitle='workorder_employee'
            // defaultGroup={DEFAULT_GROUP}
            dataItemKey="temPId"
            columns={workorderEmployeeColumn}
            name={name}
            isLoading={false}
            dataItems={dataItems}
            customGridCell={CustomGridCell}
          />
        </Grid>
        {isOpen && (
          <Dialog className="modalpopup" title={'CONFIRM DELETE'} onClose={closeDialog}>
            <div>
              <div style={{ padding: '25px 50px' }}>
                <div>Are you sure want to delete this saved item?</div>
                <LoadingIndicator isLoading={isLoadingDelete} />
                {/* <ApiErrorComponent isError={isError} errorRespnse={error} /> */}
              </div>
            </div>
            <DialogActionsBar>
              <div className=" spaceBetween">
                <button className="btn btn-sm btn-default btn-lightGrey" onClick={closeDialog} autoFocus>
                  Cancel
                </button>
                <button
                  className="btn btn-sm btn-danger btn-delete"
                  type="button"
                  style={{
                    backgroundColor: isReadOnlyPermission ? '#D7DBDD' : '#FF8A8A',
                    color: isReadOnlyPermission ? 'black' : '#fff'
                  }}
                  onClick={submitHanlder}
                  disabled={isLoadingDelete || isReadOnlyPermission}
                >
                  Delete
                </button>
              </div>
            </DialogActionsBar>
          </Dialog>
        )}
      </Grid>
    </div>
  );
};

export default WorkOrderEmployeeGrid;
